import { IonButton, IonContent, IonInput, IonPage, IonSpinner, IonToast } from "@ionic/react";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import AdminGoogleButton from "../components/AdminGoogleButton.jsx";
import AuthDivider from "../components/AuthDivider.jsx";
import FormNotice from "../components/FormNotice.jsx";
import PasswordField from "../components/PasswordField.jsx";

const blankForm = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
};

export default function CreateAdminAccountPage() {
  const { createAdminAccount, loginWithGoogleCredential } = useAuth();
  const history = useHistory();
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
  const isGoogleConfigured = googleClientId && !googleClientId.includes("local-google-client-id") && !googleClientId.includes("missing");
  const passwordsMatch = form.password.length === 0 || form.confirmPassword.length === 0 || form.password === form.confirmPassword;

  async function handleGoogle(response) {
    try {
      await loginWithGoogleCredential(response);
      history.replace("/app/schedule");
    } catch (err) {
      setError(err.message);
    }
  }

  async function submit() {
    if (!passwordsMatch) {
      setError("Passwords do not match.");

      return;
    }

    try {
      setIsSubmitting(true);
      await createAdminAccount({
        name: form.name,
        email: form.email,
        password: form.password,
      });
      history.replace("/app/schedule");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <IonPage>
      <IonContent fullscreen className="auth-screen">
        <div className="auth-card">
          <div className="auth-copy">
            <img className="auth-logo" src="/branding/metora-logo.png" alt="Metora logo" />
            <h1>Create Admin Account</h1>
            <p>You&apos;ll be the owner of your team.</p>
          </div>

          <div className="auth-form">
            <IonInput
              className="metora-input"
              label="Full Name"
              labelPlacement="stacked"
              placeholder="Enter your full name"
              value={form.name}
              onIonInput={(event) => setForm({ ...form, name: event.detail.value ?? "" })}
            />
            <IonInput
              className="metora-input"
              label="Admin Email"
              labelPlacement="stacked"
              type="email"
              placeholder="name@yourbusiness.com"
              value={form.email}
              onIonInput={(event) => setForm({ ...form, email: event.detail.value ?? "" })}
            />
            <PasswordField
              label="Password"
              value={form.password}
              visible={showPassword}
              placeholder="Enter your password"
              onIonInput={(event) => setForm({ ...form, password: event.detail.value ?? "" })}
              onToggleVisibility={() => setShowPassword((value) => !value)}
            />
            <PasswordField
              label="Confirm Password"
              value={form.confirmPassword}
              visible={showConfirmPassword}
              placeholder="Confirm your password"
              onIonInput={(event) => setForm({ ...form, confirmPassword: event.detail.value ?? "" })}
              onToggleVisibility={() => setShowConfirmPassword((value) => !value)}
            />
            {Boolean(error) && <FormNotice tone="error">{error}</FormNotice>}
            {!passwordsMatch && <FormNotice tone="error">Passwords must match.</FormNotice>}
            <IonButton expand="block" disabled={isSubmitting || !form.name || !form.email || !form.password || !form.confirmPassword || !passwordsMatch} onClick={submit}>
              {isSubmitting && <IonSpinner name="crescent" className="button-spinner" />}
              Create Account
            </IonButton>
          </div>

          <AuthDivider />
          <AdminGoogleButton
            isConfigured={isGoogleConfigured}
            onSuccess={handleGoogle}
            onError={() => setError("Google login failed")}
          />

          <p className="auth-legal-copy">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>
          <IonButton expand="block" fill="clear" className="auth-link-button" onClick={() => history.push("/login/sign-in")}>
            Already have an account? Sign In
          </IonButton>
        </div>
        <IonToast isOpen={Boolean(error)} message={error} duration={2500} onDidDismiss={() => setError("")} />
      </IonContent>
    </IonPage>
  );
}
